import { useSearchParams } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { encodeFilters, decodeFilters } from "../utils/filterSerializer";

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function useUrlSync(defaults) {
  const [searchParams, setSearchParams] = useSearchParams();

  const readRaw = () => searchParams.toString();
  const pushRaw = (qs) => setSearchParams(qs, { replace: false });

  /* ─── refs ─── */
  const lastPushed = useRef(readRaw()); // what we last wrote
  const firstRender = useRef(true);

  /* ─── seed state from URL once ─── */
  const [currentPage, setCurrentPage] = useState(
    () => Number(searchParams.get("page")) || defaults.page
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    () => Number(searchParams.get("limit")) || defaults.limit
  );
  const [addedFilters, setAddedFilters] = useState(() => {
    const decoded = decodeFilters(searchParams.get("filters"));
    return Object.keys(decoded || {}).length ? decoded : defaults.filters;
  });

  // Add tab state handling
  const [activeTab, setActiveTab] = useState(
    () => searchParams.get("tab") || defaults.tab
  );

  /* ─────────  A. URL ➜ state  ───────── */
  useEffect(() => {
    const urlPage = Number(searchParams.get("page")) || defaults.page;
    const urlLimit = Number(searchParams.get("limit")) || defaults.limit;
    const urlTab = searchParams.get("tab") || defaults.tab;

    const rawFilters = decodeFilters(searchParams.get("filters"));
    const urlFilters = Object.keys(rawFilters || {}).length
      ? rawFilters
      : defaults.filters;

    const changed =
      urlPage !== currentPage ||
      urlLimit !== itemsPerPage ||
      urlTab !== activeTab ||
      !isEqual(urlFilters, addedFilters);

    console.log("[URL→state] triggered", {
      urlPage,
      urlLimit,
      urlTab,
      urlFilters,
      changed,
    });

    if (changed) {
      setCurrentPage(urlPage);
      setItemsPerPage(urlLimit);
      setActiveTab(urlTab);
      setAddedFilters(urlFilters);
    }

    lastPushed.current = readRaw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ─────────  B. state ➜ URL  ───────── */
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      lastPushed.current = readRaw();
      console.log("[state→URL] SKIP first render");
      return;
    }

    const qp = new URLSearchParams();
    if (currentPage !== defaults.page) qp.set("page", currentPage);
    if (itemsPerPage !== defaults.limit) qp.set("limit", itemsPerPage);
    if (activeTab !== defaults.tab) qp.set("tab", activeTab);

    const filtersToEncode = isEqual(addedFilters, defaults.filters)
      ? {}
      : addedFilters;
    const filtersStr = encodeFilters(filtersToEncode);
    if (filtersStr) qp.set("filters", filtersStr);

    const next = qp.toString();
    const curr = readRaw();

    const shouldPush = next !== curr && next !== lastPushed.current;

    console.log("[state→URL] decision", {
      next,
      curr,
      last: lastPushed.current,
      shouldPush,
    });

    /* --- push only when needed --- */
    if (shouldPush) {
      pushRaw(next);
      lastPushed.current = next;
      console.log("[state→URL] PUSH", next);
    }

    /* --- prevent auto-reset to empty on first change --- */
    if (!next && curr && curr !== lastPushed.current) {
      // Don't push empty query — just update last pushed
      lastPushed.current = curr;
    }
  }, [
    currentPage,
    itemsPerPage,
    activeTab,
    addedFilters,
    defaults.page,
    defaults.limit,
    defaults.tab,
    defaults.filters,
  ]);

  /* Expose setters / state */
  return {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    addedFilters,
    setAddedFilters,
    activeTab,
    setActiveTab,
  };
}
